import ApiService from './ApiService';
import { safeErrorCode } from './errorDiagnostics';
import { jwtDecode } from 'jwt-decode';
import { getActiveClientIdForApi } from '../utils/activeClientId';
import { isLocalDevelopmentHost } from '../utils/domainUtils';
import { analyticsEvent, analyticsRoute, analyticsReferrer, analyticsUuid, sanitizeAnalyticsProperties } from './frontendAnalyticsPrivacy';

export type FrontendAnalyticsCapturePayload = {
  event: string;
  distinct_id?: string;
  properties?: Record<string, unknown>;
  timestamp?: string | number;
};

export type RouteAnalyticsContext = {
  route_segment?: string;
  route_area?: string;
  route_feature?: string;
  route_depth?: number;
  app_surface?: 'public' | 'auth' | 'admin' | 'unknown';
  is_admin_route?: boolean;
  is_auth_route?: boolean;
  is_public_route?: boolean;
};

type FrontendAnalyticsJwtContext = {
  subject?: string;
  tenantId?: string;
};

const FRONTEND_ANALYTICS_SESSION_KEY = 'orgcloud_frontend_analytics_session_id';
const FRONTEND_ANALYTICS_VISITOR_KEY = 'orgcloud_frontend_analytics_visitor_profile';

const shouldSkipFrontendAnalyticsTransport = () =>
  typeof window !== 'undefined' && isLocalDevelopmentHost(window.location.hostname);

const generateAnalyticsSessionId = () =>
  `fe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

type FrontendVisitorProfile = {
  firstSeenAt: string;
  visitCount: number;
};

export const getOrCreateFrontendAnalyticsSessionId = () => {
  if (typeof window === 'undefined') return 'fe_server';
  try {
    const existing = window.sessionStorage.getItem(FRONTEND_ANALYTICS_SESSION_KEY);
    if (existing && /^fe_[a-z0-9]{8,12}_[a-z0-9]{1,16}$/.test(existing)) return existing;
    const next = generateAnalyticsSessionId();
    window.sessionStorage.setItem(FRONTEND_ANALYTICS_SESSION_KEY, next);
    return next;
  } catch {
    return generateAnalyticsSessionId();
  }
};

const getOrCreateFrontendVisitorProfile = (): FrontendVisitorProfile => {
  if (typeof window === 'undefined') {
    return { firstSeenAt: new Date().toISOString(), visitCount: 1 };
  }
  try {
    const raw = window.localStorage.getItem(FRONTEND_ANALYTICS_VISITOR_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<FrontendVisitorProfile>;
      const current = {
        firstSeenAt: String(parsed.firstSeenAt || new Date().toISOString()),
        visitCount: Math.max(1, Number(parsed.visitCount || 1)),
      };
      return current;
    }
  } catch {
    // ignore localStorage issues
  }
  const created = { firstSeenAt: new Date().toISOString(), visitCount: 1 };
  try {
    window.localStorage.setItem(FRONTEND_ANALYTICS_VISITOR_KEY, JSON.stringify(created));
  } catch {
    // ignore localStorage issues
  }
  return created;
};

const bumpFrontendVisitorVisitCount = (): FrontendVisitorProfile => {
  const current = getOrCreateFrontendVisitorProfile();
  const next = {
    ...current,
    visitCount: Math.max(1, current.visitCount + 1),
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(FRONTEND_ANALYTICS_VISITOR_KEY, JSON.stringify(next));
    } catch {
      // ignore localStorage issues
    }
  }
  return next;
};

let frontendVisitCountInitialized = false;

const getFrontendVisitorProfileForEvent = () => {
  if (!frontendVisitCountInitialized) {
    frontendVisitCountInitialized = true;
    return bumpFrontendVisitorVisitCount();
  }
  return getOrCreateFrontendVisitorProfile();
};

const sanitizeProperties = sanitizeAnalyticsProperties;

const getColorScheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const deriveRouteAnalyticsContext = (pathname?: string): RouteAnalyticsContext => {
  const normalizedPath = analyticsRoute(pathname) || '/:id';
  const segments = normalizedPath.split('/').filter(Boolean);
  const first = segments[0] || 'root';
  const second = segments[1];
  const third = segments[2];
  const isSectionFirstAdminSurface = ['pages', 'objects', 'tools'].includes(first);

  let appSurface: RouteAnalyticsContext['app_surface'] = 'unknown';
  if (!segments.length) {
    appSurface = 'public';
  } else if (first === 'admin' || isSectionFirstAdminSurface) {
    appSurface = 'admin';
  } else if (first === 'auth') {
    appSurface = 'auth';
  } else {
    appSurface = 'public';
  }

  const routeArea =
    appSurface === 'admin'
      ? first === 'admin'
        ? second || 'dashboard'
        : first
      : appSurface === 'auth'
      ? second || first
      : first;
  const routeFeature =
    appSurface === 'admin'
      ? first === 'admin'
        ? third || second || first
        : second || third || first
      : appSurface === 'auth'
      ? third || second || first
      : second || first;

  return {
    route_segment: first,
    route_area: routeArea,
    route_feature: routeFeature,
    route_depth: segments.length,
    app_surface: appSurface,
    is_admin_route: appSurface === 'admin',
    is_auth_route: appSurface === 'auth',
    is_public_route: appSurface === 'public',
  };
};

export const buildFrontendRuntimeProperties = (
  overrides?: Record<string, unknown>
): Record<string, unknown> => {
  const visitorProfile = getFrontendVisitorProfileForEvent();
  const viewport =
    typeof window !== 'undefined'
      ? {
          viewport_w: window.innerWidth,
          viewport_h: window.innerHeight,
          screen_w: window.screen?.width,
          screen_h: window.screen?.height,
          device_pixel_ratio: window.devicePixelRatio,
          pathname: window.location.pathname,
          page: window.location.pathname,
          page_path: window.location.pathname,
          page_url: window.location.pathname,
          current_url: window.location.pathname,
          standalone:
            window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
            (window.navigator as Navigator & { standalone?: boolean }).standalone === true,
        }
      : {};

  const navigatorData =
    typeof navigator !== 'undefined'
      ? {
          language: navigator.language,
          languages: Array.isArray(navigator.languages)
            ? navigator.languages.slice(0, 5)
            : undefined,
          user_agent: navigator.userAgent,
          max_touch_points: navigator.maxTouchPoints,
        }
      : {};

  const documentData =
    typeof document !== 'undefined'
      ? {
          referrer: analyticsReferrer(document.referrer),
          source: analyticsReferrer(document.referrer),
        }
      : {};

  const intlTimeZone =
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
  const routeContext =
    typeof window !== 'undefined' ? deriveRouteAnalyticsContext(window.location.pathname) : undefined;

  return sanitizeProperties({
    client_id: getActiveClientIdForApi(),
    frontend_session_id: getOrCreateFrontendAnalyticsSessionId(),
    first_seen_at: visitorProfile.firstSeenAt,
    visit_count: visitorProfile.visitCount,
    returning_visitor: visitorProfile.visitCount > 1,
    color_scheme: getColorScheme(),
    timezone: intlTimeZone,
    ...routeContext,
    ...viewport,
    ...navigatorData,
    ...documentData,
    ...overrides,
  });
};

export const buildPageViewAnalyticsProperties = (
  overrides?: Record<string, unknown>
): Record<string, unknown> =>
  buildFrontendRuntimeProperties({
    event_source: 'frontend',
    kind: 'page_view',
    ...overrides,
  });

export const compactAnalyticsError = (value: unknown) => {
  if (!value) return undefined;
  return safeErrorCode(value);
};

const normalizeUrlForAnalytics = (rawUrl?: string | null) => {
  const href = analyticsRoute(rawUrl);
  if (!href) return {};
  let navigationIntent: string | undefined;
  try {
    const url = new URL(rawUrl!, window.location.origin);
    if (url.origin === window.location.origin) {
      navigationIntent = url.pathname.startsWith('/auth/sign-up') ? 'signup'
        : url.hash === '#pricing' ? 'pricing' : url.hash === '#contact' ? 'contact' : undefined;
    }
  } catch { /* Unrecognized destinations have no intent. */ }
  return { href, external: href === '/external', navigation_intent: navigationIntent };
};

export const extractFrontendAnalyticsJwtContext = (
  jwt?: string
): FrontendAnalyticsJwtContext => {
  const token = String(jwt || '').trim();
  if (!token) return {};
  try {
    const claims = jwtDecode<Record<string, unknown>>(token);
    const subject = String(claims.sub || '').trim() || undefined;
    const tenantId =
      String(claims['custom:client_id'] || claims.client_id || '').trim() || undefined;
    return { subject, tenantId };
  } catch {
    return {};
  }
};

const resolveAnalyticsClientId = (payload?: FrontendAnalyticsCapturePayload): string | undefined => {
  const propertyClientId = String(payload?.properties?.client_id || '').trim();
  return analyticsUuid(getActiveClientIdForApi(propertyClientId));
};

export const describeInteractionTarget = (
  target: EventTarget | null
): Record<string, unknown> | null => {
  if (typeof window === 'undefined' || !(target instanceof HTMLElement)) return null;
  const element = target.closest(
    'a,button,[role="button"],input[type="submit"],input[type="button"],summary,[data-analytics-id]'
  ) as HTMLElement | null;
  if (!element) return null;

  const hrefValue =
    element instanceof HTMLAnchorElement
      ? element.href
      : element.getAttribute('href') || element.getAttribute('data-href');
  const normalizedUrl = normalizeUrlForAnalytics(hrefValue);
  const rawTag = element.tagName.toLowerCase();
  const tagName = ['a', 'button', 'input', 'summary'].includes(rawTag) ? rawTag : 'element';
  const typeValue =
    element instanceof HTMLInputElement
      ? element.type
      : element.getAttribute('type') || undefined;
  const actionKind =
    tagName === 'a'
      ? 'link'
      : typeValue === 'submit'
      ? 'submit'
      : tagName === 'summary'
      ? 'summary'
      : 'action';
  return sanitizeProperties({
    action_kind: actionKind,
    element_tag: tagName,
    element_type: typeValue,
    target: tagName,
    component: tagName,
    selector: tagName,
    ...normalizedUrl,
  });
};

export const describeFormSubmission = (
  target: EventTarget | null
): Record<string, unknown> | null => {
  if (!(target instanceof HTMLFormElement)) return null;
  const form = target;
  const normalizedAction = normalizeUrlForAnalytics(form.getAttribute('action') || undefined);
  const inputCount = form.querySelectorAll('input,select,textarea').length;
  return sanitizeProperties({
    action_kind: 'form_submit',
    form_method: (form.getAttribute('method') || 'get').toLowerCase(),
    field_count: inputCount,
    target: 'form',
    selector: 'form',
    ...normalizedAction,
  });
};

class FrontendAnalyticsService {
  static async capture(
    jwt: string | undefined,
    payload: FrontendAnalyticsCapturePayload
  ): Promise<void> {
    if (!jwt || !analyticsEvent(payload?.event)) return;
    if (shouldSkipFrontendAnalyticsTransport()) return;
    const jwtContext = extractFrontendAnalyticsJwtContext(jwt);
    const properties = sanitizeProperties({
      ...buildFrontendRuntimeProperties(),
      ...payload.properties,
      user_subject: jwtContext.subject,
      tenant_id: jwtContext.tenantId,
    });

    if (!jwtContext.tenantId) {
      const fallbackClientId = resolveAnalyticsClientId({ ...payload, properties });
      if (!fallbackClientId) return;
      await ApiService.capturePublicAnalyticsEvent({
        event: analyticsEvent(payload.event)!,
        distinct_id:
          analyticsUuid(payload.distinct_id) ||
          `auth-fallback::${analyticsUuid(jwtContext.subject) || 'unknown'}::${getOrCreateFrontendAnalyticsSessionId()}`,
        properties: sanitizeProperties({
          ...properties,
          client_id: fallbackClientId,
          authenticated_subject: jwtContext.subject,
          auth_claims_missing_tenant: true,
        }),
      });
      return;
    }

    await ApiService.captureAnalyticsEvent(jwt, {
      event: analyticsEvent(payload.event)!,
      distinct_id: analyticsUuid(payload.distinct_id) || analyticsUuid(jwtContext.subject) || getOrCreateFrontendAnalyticsSessionId(),
      properties,
    });
  }

  static captureSafe(jwt: string | undefined, payload: FrontendAnalyticsCapturePayload): void {
    if (!jwt || !analyticsEvent(payload?.event)) return;
    void FrontendAnalyticsService.capture(jwt, payload).catch((error) => {
      console.warn('[FrontendAnalytics] capture skipped', {
        event: analyticsEvent(payload.event),
        message: compactAnalyticsError(error),
      });
    });
  }

  static async capturePublic(payload: FrontendAnalyticsCapturePayload): Promise<void> {
    if (!analyticsEvent(payload?.event)) return;
    if (shouldSkipFrontendAnalyticsTransport()) return;
    await ApiService.capturePublicAnalyticsEvent({
      event: analyticsEvent(payload.event)!,
      distinct_id:
        analyticsUuid(payload.distinct_id) || `public::${analyticsUuid(getActiveClientIdForApi()) || 'unknown'}::${getOrCreateFrontendAnalyticsSessionId()}`,
      properties: sanitizeProperties({
        ...buildFrontendRuntimeProperties(),
        ...payload.properties,
      }),
    });
  }

  static capturePublicSafe(payload: FrontendAnalyticsCapturePayload): void {
    if (!analyticsEvent(payload?.event)) return;
    void FrontendAnalyticsService.capturePublic(payload).catch((error) => {
      console.warn('[FrontendAnalytics] public capture skipped', {
        event: analyticsEvent(payload.event),
        message: compactAnalyticsError(error),
      });
    });
  }
}

export default FrontendAnalyticsService;
